import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { authPhoneUrl, AUTH_INTENT_GUIDE, GUIDE_ENTRY_ROUTE, parseAuthIntent } from '@/features/auth/constants';
import {
  resolveGuideLifecycleRoute,
  resolvePostAuthRoute,
} from '@/features/auth/utils/resolvePostAuthRoute';
import { AuthLoadingFallback } from '@/features/auth/components/AuthLoadingFallback';
import { Permission, UserRole } from '@/types';
import { STORAGE_KEYS } from '@/utils/constants';

interface ProtectedRouteProps {
  children: ReactNode;
  requireAdmin?: boolean;
  requireGuideProfile?: boolean;
  requireApprovedGuide?: boolean;
  requireOnboardingComplete?: boolean;
}

const GUIDE_AUTH_PATHS = [
  '/guide-application',
  '/guide-profile',
  '/onboarding',
  '/submission-success',
  GUIDE_ENTRY_ROUTE,
];
const AUTH_PATHS = new Set(['/auth/phone', '/auth/verify-otp']);

function resolveGuestIntent(search: string): typeof AUTH_INTENT_GUIDE | undefined {
  const fromQuery = parseAuthIntent(search);
  if (fromQuery) return fromQuery;
  try {
    const stored = sessionStorage.getItem(STORAGE_KEYS.AUTH_INTENT);
    return stored === AUTH_INTENT_GUIDE ? AUTH_INTENT_GUIDE : undefined;
  } catch {
    return undefined;
  }
}

export function ProtectedRoute({
  children,
  requireAdmin = false,
  requireGuideProfile = false,
  requireApprovedGuide = false,
  requireOnboardingComplete = false,
}: ProtectedRouteProps) {
  const {
    isAuthenticated,
    isInitializing,
    user,
    needsOnboarding: userNeedsOnboarding,
    isApprovedGuide,
    hasGuideProfile,
    permissions,
  } = useAuth();
  const location = useLocation();

  if (isInitializing) {
    return <AuthLoadingFallback />;
  }

  if (!isAuthenticated || !user) {
    const isGuidePath = GUIDE_AUTH_PATHS.some((path) => location.pathname.startsWith(path));
    return (
      <Navigate
        to={isGuidePath ? authPhoneUrl(AUTH_INTENT_GUIDE) : '/'}
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  if (requireAdmin && user.role !== UserRole.SUPER_ADMIN) {
    return <Navigate to="/" replace />;
  }

  // Every guide-side redirect comes from the one shared resolver, so a guard
  // can never disagree with the gate or with the server about where a user
  // belongs.
  const guideRoute = resolveGuideLifecycleRoute(user);

  if (requireApprovedGuide && !isApprovedGuide) {
    if (!hasGuideProfile) {
      return <Navigate to={GUIDE_ENTRY_ROUTE} replace />;
    }
    return <Navigate to={guideRoute ?? GUIDE_ENTRY_ROUTE} replace />;
  }

  if (requireGuideProfile && !hasGuideProfile) {
    return <Navigate to={GUIDE_ENTRY_ROUTE} replace />;
  }

  // An approved guide with an unfinished lifecycle should not sit on an
  // operational dashboard page.
  if (
    requireOnboardingComplete &&
    userNeedsOnboarding &&
    permissions.includes(Permission.MANAGE_GUIDE_PROFILE) &&
    guideRoute &&
    location.pathname !== guideRoute
  ) {
    return <Navigate to={guideRoute} replace />;
  }

  return <>{children}</>;
}

export function GuestRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isInitializing, user, isAdmin, permissions, postAuthRoute, accountKind } =
    useAuth();
  const location = useLocation();
  const from = (location.state as { from?: string })?.from;
  const intent = resolveGuestIntent(location.search);

  if (isInitializing) {
    return <AuthLoadingFallback />;
  }

  if (isAuthenticated && user) {
    if (intent === AUTH_INTENT_GUIDE) {
      return (
        <Navigate
          to={resolvePostAuthRoute(user, permissions, false, from, AUTH_INTENT_GUIDE)}
          replace
        />
      );
    }

    if (isAdmin || accountKind === 'super_admin') {
      return (
        <Navigate
          to={postAuthRoute && postAuthRoute !== '/' ? postAuthRoute : '/admin/dashboard'}
          replace
        />
      );
    }

    if (accountKind === 'approved_guide') {
      return (
        <Navigate
          to={postAuthRoute && postAuthRoute !== '/' ? postAuthRoute : '/dashboard'}
          replace
        />
      );
    }

    const existingGuideRoute = resolveGuideLifecycleRoute(user);
    if (existingGuideRoute) {
      return <Navigate to={existingGuideRoute} replace />;
    }

    if (from && !AUTH_PATHS.has(from) && !GUIDE_AUTH_PATHS.includes(from)) {
      return <Navigate to={from} replace />;
    }

    return (
      <Navigate to={postAuthRoute && postAuthRoute !== '/' ? postAuthRoute : '/'} replace />
    );
  }

  return <>{children}</>;
}
